import { useState } from "react";
import { type FormikHelpers } from "formik";
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { arrayMove, SortableContext } from "@dnd-kit/sortable";
import { Button as BaseButton } from "@base-ui/react/button";
import {
  IconBath,
  IconBed,
  IconKey,
  IconPhoto,
  IconPlus,
  IconSofa,
  IconToolsKitchen2,
  IconTrees,
  IconUsersGroup,
} from "@tabler/icons-react";
import EdlItemForm, { type EdlItemValues } from "./EdlItemForm";
import EdlAddSharedItem from "./EdlAddSharedItem";
import DuplicateRooms from "./DuplicateRooms";
import ClesForm from "./ClesForm";
// @ts-expect-error pure js
import useEdlItems from "../../hooks/use-edl-items";
// @ts-expect-error pure js
import { sanitizeValues } from "../../models/utils";
// @ts-expect-error pure js
import { classnames, handleAPIError } from "../../utils";
// @ts-expect-error pure js
import DraggableItem from "../UI/DraggableItem";
// @ts-expect-error pure js
import Photo from "../UI/Photo";
// @ts-expect-error pure js
import Spinner from "../UI/Spinner";
import Button from "../UI/Button";
import Text from "../UI/Text";
import Title from "../UI/Title";
import Status from "../UI/Status";
import { useToast } from "../UI/Toast/useToast";
import type { Property } from "../../models/property";
import styles from "./EdlShow.module.css";
import ButtonCard from "../UI/ButtonCard.tsx";

type EdlRoom = EdlItemValues & { id: number; photos?: { id?: number }[] };

interface SharedItemValues {
  id: number | string;
}

interface UseEdlItemsResult {
  edlItems: EdlRoom[];
  edlItemsOrder: number[];
  isLoading: boolean;
  addEdlItem: (values: unknown) => Promise<unknown>;
  updateEdlItem: (id: number | undefined, values: unknown) => Promise<unknown>;
  updateEdlItemsOrder: (order: number[]) => Promise<unknown>;
  deleteEdlItem: (id: number) => Promise<unknown>;
  addSharedItem: (values: unknown) => Promise<unknown>;
  duplicateEdlItem: (id: number) => Promise<unknown>;
  importEdlItems: (ids: number[]) => Promise<unknown>;
}

interface EdlShowProps {
  propertyId: number;
  isRealisation?: boolean;
  property: Property;
  updatePropertyCles: (
    propertyId: number,
    cles: unknown,
  ) => void | Promise<unknown>;
}

function RoomIcon({ nom }: { nom?: string }) {
  const n = (nom || "").toLowerCase();
  let Icon = IconPhoto;
  if (/cuisine/.test(n)) Icon = IconToolsKitchen2;
  else if (/bain|douche|sdb|salle d'eau/.test(n)) Icon = IconBath;
  else if (/chambre/.test(n)) Icon = IconBed;
  else if (/salon|séjour|sejour|vie|living/.test(n)) Icon = IconSofa;
  else if (/terrasse|jardin|balcon|exté|exte/.test(n)) Icon = IconTrees;
  return <Icon size={40} className={styles.roomPicto} />;
}

function RoomCard({ room, onClick }: { room: EdlRoom; onClick: () => void }) {
  const cover = room.photos?.[0];
  const elementsCount = room.items?.length ?? 0;

  return (
    <BaseButton
      nativeButton={false}
      className={styles.itemButton}
      onClick={onClick}
      render={
        <div>
          <div className={styles.roomCover}>
            {cover?.id ? (
              <Photo
                photoId={cover.id}
                isThumbnail
                type="edl"
                cover
                alt={room.nom}
                style={{ border: "none" }}
              />
            ) : (
              <RoomIcon nom={room.nom} />
            )}
            {room.is_shared && (
              <Status
                color="success"
                className={styles.sharedBadge}
                text="Partagée"
              />
            )}
          </div>
          <div className={styles.roomBody}>
            <Title variant="hecto" weight="bold" as="h3">
              {room.nom}
            </Title>
            <Text variant="deci" color="muted">
              {elementsCount} élément{elementsCount > 1 ? "s" : ""}
            </Text>
          </div>
        </div>
      }
    />
  );
}

export default function EdlShow({
  propertyId,
  isRealisation,
  property,
  updatePropertyCles,
}: EdlShowProps) {
  const [pieceOpen, setPieceOpen] = useState(false);
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [sharedOpen, setSharedOpen] = useState(false);
  const [cleOpen, setCleOpen] = useState(false);
  const toast = useToast();
  const {
    edlItems,
    edlItemsOrder,
    isLoading,
    addEdlItem,
    updateEdlItem,
    updateEdlItemsOrder,
    deleteEdlItem,
    addSharedItem,
    duplicateEdlItem,
    importEdlItems,
  } = useEdlItems(propertyId) as UseEdlItemsResult;
  const [itemToEdit, setItemToEdit] = useState<EdlRoom | null>(null);
  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(KeyboardSensor),
    useSensor(TouchSensor),
  );

  async function submitItemHandler(
    values: EdlItemValues,
    { resetForm }: FormikHelpers<EdlItemValues>,
  ) {
    const sanitizedValues = { ...sanitizeValues(values), type: "edl" };
    delete sanitizedValues.template;

    try {
      if (itemToEdit) {
        await updateEdlItem(itemToEdit.id, sanitizedValues);
      } else {
        await addEdlItem(sanitizedValues);
      }
      resetForm();
      setPieceOpen(false);
    } catch (error) {
      toast.error({ title: handleAPIError(error) });
    }
  }

  async function addSharedItemHandler(
    values: SharedItemValues,
    { resetForm }: FormikHelpers<SharedItemValues>,
  ) {
    const sanitizedValues = sanitizeValues(values);
    try {
      await addSharedItem(sanitizedValues);
      resetForm();
      setSharedOpen(false);
    } catch (error) {
      toast.error({ title: handleAPIError(error) });
    }
  }

  async function deleteHandler(id: number) {
    try {
      await deleteEdlItem(id);
      setPieceOpen(false);
    } catch (error) {
      toast.error({ title: handleAPIError(error) });
    }
  }

  async function duplicateHandler(id: number) {
    try {
      await duplicateEdlItem(id);
      setPieceOpen(false);
    } catch (error) {
      toast.error({ title: handleAPIError(error) });
    }
  }

  async function importItemsHandler(itemIds: number[]) {
    await importEdlItems(itemIds);
    setDuplicateOpen(false);
  }

  async function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;

    if (active && over && active.id !== over.id) {
      const oldIndex = edlItemsOrder.indexOf(active.id as number);
      const newIndex = edlItemsOrder.indexOf(over.id as number);
      try {
        await updateEdlItemsOrder(arrayMove(edlItemsOrder, oldIndex, newIndex));
      } catch (e) {
        console.error(e);
      }
    }
  }

  async function submitClesHandler(keys: unknown) {
    await updatePropertyCles(propertyId, keys);
    setCleOpen(false);
  }

  const otherRooms =
    edlItems?.filter((room) => room.id !== itemToEdit?.id) ?? [];

  function openNewPiece() {
    setItemToEdit(null);
    setPieceOpen(true);
  }

  function openPiece(room: EdlRoom) {
    setItemToEdit(room);
    setPieceOpen(true);
  }

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <Spinner width="3em" />
      </div>
    );
  }

  return (
    <>
      <EdlItemForm
        open={pieceOpen}
        onOpenChange={setPieceOpen}
        itemToEdit={itemToEdit}
        onSubmit={submitItemHandler}
        onDelete={deleteHandler}
        onDuplicate={duplicateHandler}
        propertyId={propertyId}
        otherRooms={otherRooms}
      />
      <ClesForm
        property={property}
        open={cleOpen}
        onOpenChange={setCleOpen}
        onSubmit={submitClesHandler}
      />
      <DuplicateRooms
        propertyId={propertyId}
        onSubmit={importItemsHandler}
        open={duplicateOpen}
        onOpenChange={setDuplicateOpen}
      />
      <EdlAddSharedItem
        open={sharedOpen}
        onOpenChange={setSharedOpen}
        onSubmit={addSharedItemHandler}
        currentItems={edlItems}
      />

      <div className={styles.actionCards}>
        <ButtonCard nativeButton={false} onClick={() => setSharedOpen(true)}>
          <div className={styles.actionCard}>
            <span className={classnames(styles.actionIcon, styles.tintOrange)}>
              <IconUsersGroup size={21} />
            </span>
            <span className={styles.actionText}>
              <Text weight="medium">Ajouter une pièce partagée</Text>
              <Text variant="deci" color="muted">
                Parties communes d'un immeuble ou d'une colocation.
              </Text>
            </span>
          </div>
        </ButtonCard>
      </div>

      {!isRealisation && (
        <Text color="muted" className={styles.help}>
          Cette page permet de configurer vos pièces. Pour réaliser un état des
          lieux, allez sur votre locataire, et cliquez sur « Réaliser un état
          des lieux ».
        </Text>
      )}

      <div className={styles.grid}>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext items={edlItemsOrder ?? []}>
            {edlItemsOrder?.map((edlId, index) => (
              <DraggableItem key={edlId} id={edlId}>
                <RoomCard
                  room={edlItems[index]}
                  onClick={() => openPiece(edlItems[index])}
                />
              </DraggableItem>
            ))}
          </SortableContext>
        </DndContext>

        <BaseButton
          nativeButton={false}
          className={classnames(styles.itemButton, styles.otherButton)}
          onClick={() => setCleOpen(true)}
          render={
            <div>
              <span className={styles.roomPicto}>
                <IconKey size={40} />
              </span>
              <Title variant="hecto" weight="bold" as="h3">
                Clés
              </Title>
            </div>
          }
        />

        <BaseButton
          nativeButton={false}
          className={classnames(
            styles.itemButton,
            styles.otherButton,
            styles.addButton,
          )}
          onClick={openNewPiece}
          render={
            <div>
              <span className={styles.addPlus}>
                <IconPlus size={22} />
              </span>
              <Text weight="medium" color="primary">
                Ajouter une pièce
              </Text>
            </div>
          }
        />
      </div>

      <div className={styles.foot}>
        <Text as="span" color="muted">
          La liste est incomplète ?
        </Text>
        <Button
          onClick={() => setDuplicateOpen(true)}
          type="button"
          variant="tertiary"
          requiresWrite
        >
          Dupliquer une pièce d'un autre logement
        </Button>
      </div>
    </>
  );
}
